import { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { KTIcon } from '../../../../helpers'
import { useRbacPermissions } from '../../../../../app/modules/auth/core/permissions'
import { axiosClientAuth } from '../../../../../app/utils'
import Swal from 'sweetalert2'

const SHOWN_ACTIONS = ['read', 'create', 'update']

type ResourcePermission = {
  resource: string
  read: boolean
  create: boolean
  update: boolean
  availableActions: string[]
  actionIds: Record<string, number[]>
}

const buildFromCatalog = (catalog: any[]): ResourcePermission[] => {
  const map: Record<string, ResourcePermission> = {}
  catalog.forEach((p: any) => {
    if (!SHOWN_ACTIONS.includes(p.action)) return
    if (!map[p.resource]) {
      map[p.resource] = { resource: p.resource, read: false, create: false, update: false, availableActions: [], actionIds: {} }
    }
    if (!map[p.resource].availableActions.includes(p.action)) {
      map[p.resource].availableActions.push(p.action)
    }
    if (!map[p.resource].actionIds[p.action]) {
      map[p.resource].actionIds[p.action] = []
    }
    map[p.resource].actionIds[p.action].push(p.id)
  })
  return Object.values(map)
}

const ModalAddRole = ({ show, setShow, onSuccess }: { show: boolean; setShow: (v: boolean) => void; onSuccess?: () => void }) => {
  const [checkbox, setCheckbox] = useState(false)
  const [roleName, setRoleName] = useState('')
  const [roleDescription, setRoleDescription] = useState('')
  const [permissions, setPermissons] = useState<ResourcePermission[]>([])
  const [errorMsg, setErrorMsg] = useState('')
  const { data: permissionCatalog = [] } = useRbacPermissions()

  useEffect(() => {
    if (permissionCatalog.length) setPermissons(buildFromCatalog(permissionCatalog))
  }, [permissionCatalog])

  const resetForm = () => {
    setShow(false)
    setRoleName('')
    setRoleDescription('')
    setPermissons(permissions.map((p) => ({ ...p, read: false, create: false, update: false })))
    setCheckbox(false)
    setErrorMsg('')
  }

  const onCheckBoxClick = (event, action: string, resource: string) => {
    setPermissons(permissions.map((p) =>
      p.resource === resource ? { ...p, [action]: event.target.checked } : p
    ))
  }

  const onSubmit = (e) => {
    e.preventDefault()
    if (!roleName.trim() || !roleDescription.trim()) {
      setErrorMsg('Name and Description are required.')
      return
    }
    const permission_ids: number[] = []
    permissions.forEach((p) => {
      SHOWN_ACTIONS.forEach((action) => {
        if (p[action] && p.actionIds[action]) {
          permission_ids.push(...p.actionIds[action])
        }
      })
    })
    if (permission_ids.length === 0) {
      setErrorMsg('At least 1 permission is required.')
      return
    }
    axiosClientAuth
      .post('/auth/roles/create/', { name: roleName, description: roleDescription, permission_ids })
      .then(() => {
        Swal.fire({ titleText: 'Success!', text: 'Role Created Successfully', icon: 'success', confirmButtonText: 'Ok' })
          .then(() => { resetForm(); onSuccess?.() })
      })
      .catch((err) => {
        Swal.fire({
          titleText: 'Error!',
          text: err?.response?.data?.message || 'Failed to create role',
          icon: 'error',
          confirmButtonText: 'Ok',
        })
      })
  }

  const selectAll = (e) => {
    setCheckbox(e.target.checked)
    setPermissons(permissions.map((p) => ({
      ...p,
      read: e.target.checked && p.availableActions.includes('read'),
      create: e.target.checked && p.availableActions.includes('create'),
      update: e.target.checked && p.availableActions.includes('update'),
    })))
  }

  return (
    <Modal
      className='modal-sticky modal-lg modal-sticky-lg modal-sticky-bottom-right'
      show={show}
      centered={true}
    >
      <div className='modal-header'>
        <h2>Add new role</h2>

        <div className='d-flex justify-content-end'>
          <button
            className='ms-auto btn btn-sm btn-icon btn-active-color-primary'
            onClick={resetForm}
          >
            <KTIcon iconName='cross' className='text-gray fs-1' />
          </button>
        </div>
      </div>

      <div className='modal-body py-lg-0 px-lg-0'>
        <div className='modal-body scroll-y mx-lg-5 my-7'>
          <form className='form' onSubmit={onSubmit}>


            <div className='d-flex flex-column scroll-y me-n7 pe-7'>
              <div className='fv-row mb-10'>
                <label className='fs-5 fw-bold form-label mb-2'>
                  <span className='required'>Role name</span>
                </label>
                <input
                  className='form-control form-control-solid'
                  placeholder='Enter a role name'
                  name='role_name'
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                />
              </div>
              <div className='fv-row mb-10'>
                <label className='fs-5 fw-bold form-label mb-2'>
                  <span className='required'>Role description</span>
                </label>
                <input
                  className='form-control form-control-solid'
                  placeholder='Enter a role description'
                  name='description'
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                />
              </div>
              <div className='fv-row'>
                <label className='fs-5 fw-bold form-label mb-2'>Role Permissions</label>
                <div className='table-responsive'>
                  <table className='table align-middle table-row-dashed fs-6 gy-5'>
                    <tbody className='text-gray-600 fw-semibold'>
                      <tr>
                        <td className='text-gray-800'>
                          Administrator Access
                          <i
                            className='fas fa-exclamation-circle ms-1 fs-7'
                            data-bs-toggle='tooltip'
                            title='Allows a full access to the system'
                          />
                        </td>
                        <td>
                          <label className='form-check form-check-custom form-check-solid me-9'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              checked={checkbox}
                              onChange={(e) => selectAll(e)}
                            />
                            <span
                              className='form-check-label'
                            // htmlFor="kt_roles_select_all"
                            >
                              Select all
                            </span>
                          </label>
                        </td>
                      </tr>
                      {permissions?.map((permission) => (
                        <tr key={permission.resource}>
                          <td className='text-gray-800 text-capitalize'>{permission.resource.replace(/_/g, ' ')}</td>
                          <td>
                            <div className='d-flex'>
                              {(['read', 'create', 'update'] as const).map((action) =>
                                permission.availableActions.includes(action) ? (
                                  <label key={action} className='form-check form-check-sm form-check-custom form-check-solid me-5 me-lg-20'>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      checked={permission[action]}
                                      onChange={(e) => onCheckBoxClick(e, action, permission.resource)}
                                    />
                                    <span className='form-check-label text-capitalize'>{action}</span>
                                  </label>
                                ) : null
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {errorMsg && <div className="mb-5 alert alert-danger">
              <div className="alert-text font-weight-bold">{errorMsg}</div>
            </div>}
            <div className='text-center pt-15'>
              <button type='reset' className='btn btn-light me-3' onClick={() => resetForm()}>
                Discard
              </button>
              <button type='submit' className='btn btn-primary'>
                <span className='indicator-label'>Submit</span>
                <span className='indicator-progress'>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2' />
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  )
}

export default ModalAddRole
